<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, type VNodeRef, watch } from 'vue'
import {
  EbomBulkAllocatePartTreeDataQuery,
  EbomBulkAllocatePartTreeDataQueryVariables,
} from '@/generated/graphql'
import { sortTree } from '@/app'
import EbomBulkAllocatePartTreeNode from '@/components/ebom/EbomBulkAllocatePartTreeNode.vue'
import useIdSelection from '@/composables/useIdSelection'
import { openTreeRows, type TreeRow } from '@/utils/treeRows'

const props = defineProps<{
  ebomId: string
}>()

const fetchQuery = useQuery<
  EbomBulkAllocatePartTreeDataQuery,
  EbomBulkAllocatePartTreeDataQueryVariables
>(
  gql`
    query EbomBulkAllocatePartTreeData($id: ID!) {
      article {
        ebom(id: $id) {
          ebomParts {
            id
            parentId
            articleNumber
            name
            revision
            unit
            milliQuantity
            position
            article {
              id
            }
            articlePath {
              articleNumbers
            }
            stepAllocations {
              id
              milliQuantity
              article {
                unit
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    id: props.ebomId,
  }),
)
const ebomParts = computed(() => fetchQuery.result.value?.article.ebom.ebomParts || [])

type LocalPart = EbomBulkAllocatePartTreeDataQuery['article']['ebom']['ebomParts'][0]
export type EbomBulkAllocatePartNode = {
  ebomPart: LocalPart
  isSelectable: boolean
  netAllocationMilliQuantity: number
  isOrHasAbberation: boolean
  children: EbomBulkAllocatePartNode[]
}

const sortNodes = (nodes: EbomBulkAllocatePartNode[]) =>
  sortTree(
    nodes,
    (n) => n.children.length > 0,
    (n) => n.ebomPart.position || `${n.ebomPart.articleNumber}/${n.ebomPart.revision}`,
  )
const rootNodes = computed<EbomBulkAllocatePartNode[]>(() => {
  const partsByParentId = new Map<string, LocalPart[]>()
  for (const part of ebomParts.value) {
    if (!part.parentId) continue
    const siblings = partsByParentId.get(part.parentId)
    if (siblings) siblings.push(part)
    else partsByParentId.set(part.parentId, [part])
  }
  return sortNodes(
    ebomParts.value.filter((p) => !p.parentId).map((p) => createNode(partsByParentId, p)),
  )
})
function createNode(
  partsByParentId: Map<string, LocalPart[]>,
  part: LocalPart,
): EbomBulkAllocatePartNode {
  const children = (partsByParentId.get(part.id) ?? []).map((p) => createNode(partsByParentId, p))
  const netAllocationMilliQuantity = part.article
    ? part.stepAllocations.reduce((agg, c) => agg + c.milliQuantity, 0) - part.milliQuantity
    : 0
  const isOrHasAbberation =
    netAllocationMilliQuantity != 0 || children.some((c) => c.isOrHasAbberation)

  return {
    ebomPart: part,
    isSelectable: !!part.article,
    netAllocationMilliQuantity,
    isOrHasAbberation,
    children: sortNodes(children),
  }
}

const { selectedIds: openAssemblyIds, selectId: markAssemblyAsOpen } = useIdSelection(
  'ebom-bulk-allocation-open-assemblies',
)
watch(rootNodes, (v) => {
  if (v.length != 1 || openAssemblyIds.value.includes(v[0].ebomPart.id)) {
    return
  }

  markAssemblyAsOpen(v[0].ebomPart.id)
})

const openIds = computed(() => new Set(openAssemblyIds.value))
const rows = computed(() => openTreeRows(rootNodes.value, (n) => openIds.value.has(n.ebomPart.id)))

const siblingsById = computed(() => {
  const siblingsById = new Map<string, EbomBulkAllocatePartNode[]>()
  const visit = (siblings: EbomBulkAllocatePartNode[]) =>
    siblings.forEach((n) => {
      siblingsById.set(n.ebomPart.id, siblings)
      visit(n.children)
    })
  visit(rootNodes.value)
  return siblingsById
})

const { selectId } = useIdSelection('ebom-bulk-allocation-parts')
let rangeStartId: string | undefined
const selectRange = (endId: string) => {
  const siblings = siblingsById.value.get(endId) ?? []
  const start = siblings.findIndex((n) => n.ebomPart.id == rangeStartId)
  const end = siblings.findIndex((n) => n.ebomPart.id == endId)
  if (start < 0 || end < 0) {
    return
  }

  siblings
    .slice(Math.min(start, end), Math.max(start, end) + 1)
    .forEach((n) => selectId(n.ebomPart.id))
}
</script>

<template>
  <div>
    <v-virtual-scroll
      :items="rows"
      :item-key="(row) => row.node.ebomPart.id"
      :item-height="48"
      :renderless="true"
    >
      <template
        #default="{
          item: row,
          itemRef,
        }: {
          item: TreeRow<EbomBulkAllocatePartNode>
          itemRef?: VNodeRef
        }"
      >
        <ebom-bulk-allocate-part-tree-node
          :ref="itemRef"
          :node="row.node"
          :depth="row.depth"
          @start-selection="(id: string) => (rangeStartId = id)"
          @end-selection="selectRange"
        />
      </template>
    </v-virtual-scroll>
  </div>
</template>

<style scoped lang="scss"></style>
